import { useState, type ReactNode } from "react";
import { useRouter } from '#app/lib/hooks/router-compat';
import {
  Box,
  Button,
  Icon,
  Text,
  type IconName,
} from "@salestrak/ui";
import { StatusDot, statusDotToken } from "./admin-ui";
import { useAdminData } from "./use-admin-data";
import { useAdminHealth } from "./use-admin-health";
import { OverviewScreen } from "./overview-screen";
import { SuperusersScreen } from "./superusers-screen";
import { FeatureFlagsScreen } from "./feature-flags-screen";
import { IntegrationsScreen } from "./integrations-screen";
import { WorkspacesScreen } from "./workspaces-screen";
import { SystemHealthScreen } from "./system-health-screen";
import { ScheduledTasksScreen } from "./scheduled-tasks-screen";
import { useAdminTasks } from "./use-admin-tasks";
import type { Omittable } from "@salestrak/ui";

export type AdminScreen =
  | "overview"
  | "superusers"
  | "flags"
  | "integrations"
  | "workspaces"
  | "health"
  | "tasks";

const SCREEN_TITLES: Record<AdminScreen, string> = {
  overview: "Overview",
  superusers: "Superusers",
  flags: "Feature flags",
  integrations: "Integrations",
  workspaces: "Workspaces",
  health: "System health",
  tasks: "Scheduled tasks",
};

function SectionLabel({ children }: { children: string }) {
  return (
    <Box paddingX="md" paddingTop="md" paddingBottom="2xs">
      <Text size="2xs" weight="semibold" variant="muted">
        {children.toUpperCase()}
      </Text>
    </Box>
  );
}

function CountTag({ value }: { value: number }) {
  return (
    <Text size="xs" variant="muted" mono>
      {value}
    </Text>
  );
}

/**
 * Nav row for this console's own column. It is NOT a Sidebar row:
 * `SidebarMenuButton` calls `useSidebar()` unconditionally and throws outside a
 * `SidebarProvider`, and this column lives inside a page. So the row is a plain
 * ghost Button.
 */
function NavRow({
  icon,
  label,
  isActive,
  trailing,
  onClick,
}: Omittable<{
  icon: IconName;
  label: string;
  isActive: boolean;
  trailing?: ReactNode;
  onClick: () => void;
}>) {
  return (
    <Button
      variant={isActive ? "secondary" : "ghost"}
      size="sm"
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      className="w-full justify-start"
    >
      <Icon name={icon} size="sm" color={isActive ? undefined : "muted"} />
      <Text size="sm" weight={isActive ? "medium" : "normal"}>
        {label}
      </Text>
      {trailing ? (
        <Box grow direction="row" justify="end">
          {trailing}
        </Box>
      ) : null}
    </Button>
  );
}

export function AdminConsole({ onClose }: { onClose?: () => void }) {
  const router = useRouter();
  const [screen, setScreen] = useState<AdminScreen>("overview");
  const data = useAdminData();
  const health = useAdminHealth();
  const tasks = useAdminTasks();

  const close = onClose ?? (() => router.replace("/"));

  const healthDot = statusDotToken(
    health.total > 0,
    health.downCount > 0,
    health.degradedCount > 0,
  );
  // `tasks.mode` stays null until a response lands, so "not disabled" is not
  // the same as "running".
  const tasksDot = statusDotToken(
    tasks.mode != null,
    tasks.failedCount > 0,
    tasks.mode === "disabled" || tasks.overdueCount > 0,
  );

  return (
    <Box direction="column" fill="flex" minHeight={0} background="background">
      {/* Titlebar */}
      <Box
        direction="row"
        align="center"
        gap="sm"
        paddingX="md"
        height={44}
        border="bottom"
        background="sunken"
      >
        <Box width={20} height={20} rounded="sm" background="muted" align="center" justify="center">
          <Icon name="shield" size="xs" color="accent" />
        </Box>
        <Text size="sm" weight="semibold">
          SalesTrak Admin
        </Text>
        <Text size="2xs" weight="semibold" mono color="violet">
          SUPERUSER
        </Text>
        <Box grow />
        <Button variant="ghost" size="icon-sm" aria-label="Close" onClick={close}>
          <Icon name="x" size="sm" color="muted" />
        </Button>
      </Box>

      <Box fill="flex" direction="row" minHeight={0}>
        {/* Sidebar */}
        <Box width={236} border="right" background="sunken" minHeight={0} overflow="auto">
          <Box paddingX="xs" paddingTop="sm">
            <NavRow
              icon="layout-dashboard"
              label="Overview"
              isActive={screen === "overview"}
              onClick={() => setScreen("overview")}
            />
          </Box>

          <SectionLabel>Access</SectionLabel>
          <Box paddingX="xs">
            <NavRow
              icon="shield"
              label="Superusers"
              isActive={screen === "superusers"}
              trailing={<CountTag value={data.superusers.length} />}
              onClick={() => setScreen("superusers")}
            />
          </Box>

          <SectionLabel>Platform</SectionLabel>
          <Box paddingX="xs" gap="2xs">
            <NavRow
              icon="flag"
              label="Feature flags"
              isActive={screen === "flags"}
              trailing={<CountTag value={data.flags.length} />}
              onClick={() => setScreen("flags")}
            />
            <NavRow
              icon="layout-dashboard"
              label="Integrations"
              isActive={screen === "integrations"}
              trailing={<CountTag value={data.providers.length} />}
              onClick={() => setScreen("integrations")}
            />
            <NavRow
              icon="layout-grid"
              label="Workspaces"
              isActive={screen === "workspaces"}
              trailing={<CountTag value={data.workspaces.length} />}
              onClick={() => setScreen("workspaces")}
            />
          </Box>

          <SectionLabel>System</SectionLabel>
          <Box paddingX="xs" gap="2xs">
            <NavRow
              icon="activity"
              label="System health"
              isActive={screen === "health"}
              trailing={<StatusDot background={healthDot} />}
              onClick={() => setScreen("health")}
            />
            <NavRow
              icon="calendar-clock"
              label="Scheduled tasks"
              isActive={screen === "tasks"}
              trailing={<StatusDot background={tasksDot} />}
              onClick={() => setScreen("tasks")}
            />
          </Box>
        </Box>

        {/* Main */}
        <Box fill="flex" minHeight={0} direction="column">
          {/* Breadcrumb */}
          <Box direction="row" align="center" gap="xs" paddingX="lg" height={46} border="bottom">
            <Text size="sm" variant="muted">
              Admin
            </Text>
            <Text size="sm" variant="muted">
              /
            </Text>
            <Text size="sm" weight="medium">
              {SCREEN_TITLES[screen]}
            </Text>
          </Box>

          <Box direction="column" fill="flex" minHeight={0}>
            {screen === "overview" ? (
              <OverviewScreen
                superuserCount={data.superusers.length}
                flagCount={data.flags.length}
                workspaceCount={data.workspaces.length}
                servicesUp={health.upCount}
                servicesTotal={health.total}
                navigate={setScreen}
              />
            ) : null}
            {screen === "superusers" ? (
              <SuperusersScreen
                superusers={data.superusers}
                loading={data.superusersLoading}
                memberships={data.memberships}
                workspaces={data.workspaces}
                refetch={data.refetchSuperusers}
              />
            ) : null}
            {screen === "flags" ? (
              <FeatureFlagsScreen
                flags={data.flags}
                loading={data.flagsLoading}
                workspaces={data.workspaces}
                refetchFlags={data.refetchFlags}
              />
            ) : null}
            {screen === "integrations" ? (
              <IntegrationsScreen
                providers={data.providers}
                loading={data.providersLoading}
                refetchProviders={data.refetchProviders}
              />
            ) : null}
            {screen === "workspaces" ? (
              <WorkspacesScreen
                workspaces={data.workspaces}
                loading={data.workspacesLoading}
              />
            ) : null}
            {screen === "tasks" ? (
              <ScheduledTasksScreen
                tasks={tasks.tasks}
                runs={tasks.runs}
                mode={tasks.mode}
                registered={tasks.registered}
                loading={tasks.loading}
                error={tasks.error}
                lastChecked={tasks.lastChecked}
                refresh={tasks.refresh}
                failedCount={tasks.failedCount}
                overdueCount={tasks.overdueCount}
              />
            ) : null}
            {screen === "health" ? (
              <SystemHealthScreen
                services={health.services}
                checking={health.checking}
                lastChecked={health.lastChecked}
                recheck={health.recheck}
                total={health.total}
                upCount={health.upCount}
                degradedCount={health.degradedCount}
                downCount={health.downCount}
                avgMs={health.avgMs}
              />
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
